import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RestoreIconComponent } from '../../../icons/restore-icon/restore-icon.component';
import { RouterModule } from '@angular/router';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { ErrorMessageQuery } from '../../../../../shared/models/error-messages/error-message-query';
import { IfPolicyNotSatisfiedDirective } from '../../../../directives/if-policy-not-satisfied.directive';
import { IfPolicySatisfiedDirective } from '../../../../directives/if-policy-satisfied.directive';
import { Policy } from '../../../../../shared/models/info/policy';
import { SearchIconComponent } from '../../../icons/search-icon/search-icon.component';
import { PreselectionService } from '../../../../services/preselection.service';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { Subscription } from 'rxjs';
import { ErrorMessageFilterComponent } from '../error-message-filter/error-message-filter.component';
import { FilterIconComponent } from '../../../icons/filter-icon/filter-icon.component';
import { HelpPopoverComponent } from '../../../common/popover/help-popover/help-popover.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Paragraph } from '../../../../../shared/models/internal/popover/paragraph';
import { initFlowbite } from 'flowbite';
import { buildTranslationStateColorsHelp } from '../../../../../shared/internal/build-translation-state-colors-help';
import { LanguageSelectionComponent } from '../../../common/language/language-selection/language-selection.component';
import { SwPartNumberSelectionComponent } from '../../../common/sw-part-number/sw-part-number-selection/sw-part-number-selection.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';

@Component({
  selector: 'app-error-message-table-search',
  imports: [
    CommonModule,
    FormsModule,
    RestoreIconComponent,
    RouterModule,
    IfPolicyNotSatisfiedDirective,
    IfPolicySatisfiedDirective,
    SearchIconComponent,
    ErrorMessageFilterComponent,
    FilterIconComponent,
    HelpPopoverComponent,
    LanguageSelectionComponent,
    SwPartNumberSelectionComponent,
    AgentActionDirective,
  ],
  templateUrl: './error-message-table-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-message-table-search.component.scss',
})
export class ErrorMessageTableSearchComponent implements OnInit, OnDestroy {
  @Input() public recyclingModeEnabled = false;
  @Output() public searchTriggered: EventEmitter<ErrorMessageQuery> = new EventEmitter<ErrorMessageQuery>();

  public searchQuery: ErrorMessageQuery = {
    searchPattern: '',
    searchInDetailsOnTerminal: true,
    searchInHeaderOnTerminal: true,
    searchInCode: true,
    hasCategoryInfo: true,
    hasCategoryWarning: true,
    hasCategoryAlert: true,
    detailsOnTerminalEmpty: false,
  } as ErrorMessageQuery;
  public policy = Policy;
  public filterVisible = false;
  public paragraphs: Paragraph[] = [];

  private swPartNumberService = inject(SwPartNumberService);
  private preselectionService = inject(PreselectionService);
  private readonly sanitizer = inject(DomSanitizer);
  private swPartNumberChangeSubscription: Subscription | undefined;

  public ngOnInit(): void {
    this.paragraphs = buildTranslationStateColorsHelp(this.sanitizer);
    initFlowbite();

    this.searchQuery.searchPattern = this.preselectionService.getSearchTermAndClear(PreselectionType.ErrorMessage);

    this.swPartNumberChangeSubscription = this.swPartNumberService.swPartNumberChangeSubject.subscribe({
      next: () => this.search(),
    });
  }

  public ngOnDestroy(): void {
    if (this.swPartNumberChangeSubscription) {
      this.swPartNumberChangeSubscription.unsubscribe();
    }
  }

  public search(): void {
    this.searchTriggered.emit(this.searchQuery);
  }

  public searchByKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  public toggleFilter(): void {
    this.filterVisible = !this.filterVisible;
  }

  public filterApplied(query: ErrorMessageQuery): void {
    query.searchPattern = this.searchQuery.searchPattern;
    query.page = this.searchQuery.page;
    query.pageSize = this.searchQuery.pageSize;
    query.orderBy = this.searchQuery.orderBy;
    query.orderByDescending = this.searchQuery.orderByDescending;
    this.searchQuery = { ...query };
    this.searchTriggered.emit(this.searchQuery);
  }

  public filtersAreSet(): boolean {
    return (
      this.searchQuery.hasCategoryInfo === false ||
      this.searchQuery.hasCategoryWarning === false ||
      this.searchQuery.hasCategoryAlert === false ||
      this.searchQuery.searchInCode === false ||
      this.searchQuery.searchInDetailsOnTerminal === false ||
      this.searchQuery.searchInHeaderOnTerminal === false ||
      this.searchQuery.detailsOnTerminalNotEmpty !== undefined ||
      (this.searchQuery.translationStatesTerminal?.length ?? 0) > 0 ||
      this.searchQuery.assignedDtcs !== undefined ||
      this.searchQuery.mainLanguageTerminalFieldsExportedForReview === true ||
      this.searchQuery.mainLanguageTerminalFieldsReviewed !== undefined ||
      this.searchQuery.changesBy !== undefined
    );
  }

  private clearSearch(): void {
    this.searchQuery.searchPattern = '';
    this.searchTriggered.emit(this.searchQuery);
  }
}
